// Pure date math for project templates (SPEC §6.1, §6.2). Offsets are working days: day 0 is the
// first working day on or after the anchor, weekends and public holidays never count.

export type DateBase = "PROJECT" | "BUCKET";

export type ScheduleBucket = {
  startDay: number;
  durationDays: number;
  cards: { startBase: DateBase; startDay: number | null; dueBase: DateBase; dueDay: number | null }[];
};

const DAY_MS = 86_400_000;

export const dayKey = (d: Date) => d.toISOString().slice(0, 10);

/** Midnight UTC of a "YYYY-MM-DD" string, or null when it is not a real date. */
export function parseDay(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const d = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(d.getTime()) || dayKey(d) !== value ? null : d;
}

export function isWorkingDay(d: Date, holidays: ReadonlySet<string>) {
  const dow = d.getUTCDay();
  return dow !== 0 && dow !== 6 && !holidays.has(dayKey(d));
}

/** First working day on or after `d`. */
export function nextWorkingDay(d: Date, holidays: ReadonlySet<string>): Date {
  let cur = new Date(d.getTime());
  while (!isWorkingDay(cur, holidays)) cur = new Date(cur.getTime() + DAY_MS);
  return cur;
}

/** `days` working days after the first working day on or after `from` (0 = that day itself). */
export function addWorkingDays(from: Date, days: number, holidays: ReadonlySet<string>): Date {
  let cur = nextWorkingDay(from, holidays);
  for (let left = Math.max(0, days); left > 0; left--) cur = nextWorkingDay(new Date(cur.getTime() + DAY_MS), holidays);
  return cur;
}

export type ScheduledBucket = { start: Date; end: Date; cards: { start: Date; due: Date }[] };

/**
 * Bucket window = startDay for durationDays working days. A card with no start begins with its
 * bucket, one with no due date ends with it; relative days count from the project start or the bucket start.
 */
export function scheduleTemplate(buckets: ScheduleBucket[], projectStart: Date, holidays: ReadonlySet<string>): ScheduledBucket[] {
  const first = nextWorkingDay(projectStart, holidays);
  return buckets.map((b) => {
    const start = addWorkingDays(first, b.startDay, holidays);
    const end = addWorkingDays(start, Math.max(b.durationDays - 1, 0), holidays);
    const anchor = (base: DateBase) => (base === "PROJECT" ? first : start);
    return {
      start,
      end,
      cards: b.cards.map((c) => {
        const cardStart = c.startDay === null ? start : addWorkingDays(anchor(c.startBase), c.startDay, holidays);
        const rawDue = c.dueDay === null ? end : addWorkingDays(anchor(c.dueBase), c.dueDay, holidays);
        return { start: cardStart, due: rawDue < cardStart ? cardStart : rawDue };
      }),
    };
  });
}

/** "week 2" or "week 2–3" for a window of working days (5 per week), shown as a hint in the editor. */
export function weekLabel(startDay: number, durationDays: number): string {
  const from = Math.floor(startDay / 5) + 1;
  const to = Math.floor((startDay + Math.max(durationDays, 1) - 1) / 5) + 1;
  return from === to ? `week ${from}` : `week ${from}–${to}`;
}
